import * as React from "react";
import { NumberField as BaseNumberField } from "@base-ui/react/number-field";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { Button, FormControl, FormHelperText, InputLabel, OutlinedInput, styled } from "~/common/mui";
import { useTranslation } from "react-i18next";
import { useNumberFieldLogic, type BaseNumberFieldProps } from "./useNumberFieldLogic";
import { FORMATTING_LOCALE_CODE } from "~/utils/constants";
import ErrorIcon from "@mui/icons-material/Error";

export type NumberFieldCompactProps = BaseNumberFieldProps;

export default function NumberFieldCompact({
  id: idProp,
  label,
  error,
  size = "medium",
  helperText,
  step = 1,
  onValueChange,
  disabled,
  roundToStep = true,
  showErrorIcon = false,
  ...other
}: NumberFieldCompactProps & {
  label?: React.ReactNode;
  size?: "small" | "medium";
  error?: boolean;
}) {
  const { t } = useTranslation();

  const { handleValueChange } = useNumberFieldLogic({
    step,
    min: other.min,
    max: other.max,
    disabled,
    onValueChange,
  });

  let id = React.useId();
  if (idProp) {
    id = idProp;
  }

  return (
    <BaseNumberField.Root
      {...other}
      {...(!roundToStep && { step })}
      disabled={disabled}
      locale={FORMATTING_LOCALE_CODE}
      onValueChange={roundToStep ? handleValueChange : onValueChange}
      render={(props, state) => (
        <FormControl
          fullWidth
          size={size}
          ref={props.ref}
          disabled={state.disabled}
          required={state.required}
          error={error}
          variant="outlined"
        >
          {props.children}
        </FormControl>
      )}
    >
      <InputLabel
        variant="standard"
        disabled={disabled}
        htmlFor={id}
        size={size}
        shrink
        sx={{
          position: "relative",
        }}
      >
        {label}
      </InputLabel>

      <InputContainer>
        <BaseNumberField.Decrement
          render={
            <DecrementButton color="primaryContainer" aria-label={t("common.input.quantity.decrement")} size={size} />
          }
        >
          <RemoveIcon fontSize={size} />
        </BaseNumberField.Decrement>

        <BaseNumberField.Input
          id={id}
          render={(props, state) => (
            <StyledInput
              size={size}
              inputRef={props.ref}
              disabled={state.disabled}
              value={state.inputValue}
              onBlur={(e) => {
                props.onBlur?.(e);
                other.onBlur?.();
              }}
              onChange={props.onChange}
              onKeyUp={props.onKeyUp}
              onKeyDown={props.onKeyDown}
              onFocus={props.onFocus}
              endAdornment={
                error &&
                showErrorIcon && (
                  <ErrorIcon
                    color="error"
                    fontSize="medium"
                    sx={{
                      mr: 1,
                    }}
                  />
                )
              }
              slotProps={{
                input: {
                  ...props,
                  sx: {
                    textAlign: "center",
                  },
                },
              }}
            />
          )}
        />
        <BaseNumberField.Increment
          render={
            <IncrementButton color="primaryContainer" aria-label={t("common.input.quantity.increment")} size={size} />
          }
        >
          <AddIcon fontSize={size} />
        </BaseNumberField.Increment>
      </InputContainer>
      <FormHelperText>{helperText}</FormHelperText>
    </BaseNumberField.Root>
  );
}

const getButtonWidth = (size?: "small" | "medium") => (size === "small" ? "40px" : "56px");

const createStepButton = (side: "decrement" | "increment") =>
  styled(Button, {
    shouldForwardProp: (prop) => prop !== "size",
  })<{ size?: "small" | "medium" }>(({ size, theme }) => ({
    maxWidth: getButtonWidth(size),
    minWidth: getButtonWidth(size),
    borderRadius: "4px",
    border: `1px solid ${theme.palette.divider}`,
    overflow: "hidden",
    "&.MuiButtonBase-root": {
      borderRadius: "4px",
    },

    ...(side === "decrement" && {
      borderTopRightRadius: "0px !important",
      borderBottomRightRadius: "0px !important",
      borderRight: 0,
      "&.Mui-disabled": {
        borderTopRightRadius: "0px !important",
        borderBottomRightRadius: "0px !important",
        borderRight: 0,
      },
    }),

    ...(side === "increment" && {
      borderTopLeftRadius: "0px !important",
      borderBottomLeftRadius: "0px !important",
      borderLeft: 0,
      "&.Mui-disabled": {
        borderTopLeftRadius: "0px !important",
        borderBottomLeftRadius: "0px !important",
        borderLeft: 0,
      },
    }),
  }));

const DecrementButton = createStepButton("decrement");
const IncrementButton = createStepButton("increment");

const InputContainer = styled("div")({
  display: "flex",
  overflow: "hidden",
  borderRadius: "4px",
});

const StyledInput = styled(OutlinedInput)({
  pr: 0,
  width: "100%",
  "& .MuiOutlinedInput-notchedOutline": {
    borderRadius: 0,
  },
});
